// Helpers for taggable curriculum elements (PYP / DP / MYP / UBD).
// Extracted from src/AppComponents/Post/modules/PostModule.js — these helpers
// are curriculum-element logic, not Post-domain logic, and they were causing a
// circular dependency between PostModule and modules/ElementValueUtils.
//
// Route / feature-flag helpers below are inlined locally (originally lived in
// src/AppComponents/Post/utils.js and src/routes/modules/LoginModule/LoginUtils.js)
// to keep this shared module from reaching back into Post-feature folders.
// Do NOT replace these with imports from Post/utils — that re-introduces the
// dependency this file was created to remove.

import _ from "lodash";
import ACLStore from "lib/aclStore";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
} from "Constants/stringConstants";
import {
  TAGGABLE_PYP_ELEMENTS,
  TAGGABLE_PYP_ELEMENTS_V2,
  TAGGABLE_DP_ELEMENTS,
  TAGGABLE_DP_ELEMENTS_V2,
  TAGGABLE_MYP_ELEMENTS,
  TAGGABLE_MYP_ELEMENTS_V2,
  TAGGABLE_UBD_ELEMENTS,
  TAGGABLE_ELEMENTS_FIELD_LIST,
} from "Constants/taggableElements";
import { checkEmptyElementValue } from "modules/ElementValueUtils";

const isPublicPlatformPostRoute = () =>
  _.includes(window.location.href, "platform/public/post");

const isPortfolioParityEnabled = () =>
  ACLStore.can("FeatureFlag:PortfolioParity") ||
  _.includes(window.location.href, "/portfolio-review");

const isPortfolioReviewSetupEnabledAndIsReviewRoute = () =>
  _.includes(window.location.href, "/portfolio-review") &&
  ACLStore.can("EnablePortfolioReviewer");

const getIsNewGoalTaggingEnabled = () =>
  ACLStore.can("FeatureFlag:EnableNewGoalTagging") ||
  _.includes(window.location.href, "/portfolio-review");

const getIsSpecifiedConceptsTaggingEnabled = () =>
  ACLStore.can("FeatureFlag:EnableSpecifiedConceptTagging");

export const getDynamicPlannerElementLabelStatus = ({ item }) => {
  return (
    (item.dynamicPlannerElementLabelPerm &&
      ACLStore.can(item.dynamicPlannerElementLabelPerm)) ||
    item.isDynamicPlannerElementLabel
  );
};

export const getFieldList = ({ curriculumProgramType }) => {
  return _.get(TAGGABLE_ELEMENTS_FIELD_LIST, curriculumProgramType, {}) || {};
};

export const getTaggableElements = ({
  curriculumProgramType,
  unitPlan,
  plannerElementSets,
  excludedElements = [],
  postTaggedEntity = [],
} = {}) => {
  let taggableElements = [];
  const isJournalRoute =
    _.includes(window.location.href, "/journal") ||
    _.includes(window.location.href, "/studentFeed") ||
    _.includes(window.location.href, "/family-portfolio-summary");
  const isClassStremResponseRoute = _.includes(
    window.location.href,
    "/classroom-details"
  );
  const isPublicPost = isPublicPlatformPostRoute();
  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_UBD:
      {
        if (_.isEmpty(plannerElementSets)) {
          taggableElements = TAGGABLE_UBD_ELEMENTS;
        } else {
          taggableElements = getTaggableElementsWithDynamicLabels({
            elements: TAGGABLE_UBD_ELEMENTS,
            plannerElementSets,
          });
        }
      }
      break;

    case CURRICULUM_TYPE_MYP:
      {
        let updateTaggableMypElements = _.cloneDeep(TAGGABLE_MYP_ELEMENTS);
        if (!getIsSpecifiedConceptsTaggingEnabled() && !isPublicPost) {
          updateTaggableMypElements = _.filter(
            updateTaggableMypElements,
            ({ value }) => !_.isEqual(value, "specifiedConcepts")
          );
        }
        if (_.isEmpty(plannerElementSets)) {
          if (
            _.get(unitPlan, "unitType.value") == "ibMYPInterdisciplinary" &&
            !getIsNewGoalTaggingEnabled()
          ) {
            const elements = _.cloneDeep(updateTaggableMypElements);
            return _.filter(
              elements,
              item =>
                !_.includes(
                  [
                    "learningStandardMYP",
                    "objectivesMYP",
                    "relatedConceptsMYP",
                  ],
                  item.value
                )
            );
          } else if (getIsNewGoalTaggingEnabled()) {
            _.forEach(postTaggedEntity, postEntity => {
              if (
                _.get(postEntity, "entity.unitType.value") ==
                "ibMYPInterdisciplinary"
              ) {
                const elements = _.cloneDeep(updateTaggableMypElements);
                return _.filter(
                  elements,
                  item =>
                    !_.includes(
                      [
                        "learningStandardMYP",
                        "objectivesMYP",
                        "relatedConceptsMYP",
                      ],
                      item.value
                    )
                );
              }
            });
          }
          taggableElements = updateTaggableMypElements;
        } else {
          taggableElements = getTaggableElementsWithDynamicLabels({
            elements: updateTaggableMypElements,
            plannerElementSets,
          });
        }
      }
      break;

    case CURRICULUM_TYPE_DP:
      {
        taggableElements =
          isPortfolioParityEnabled() &&
          (isJournalRoute ||
            isClassStremResponseRoute ||
            isPortfolioReviewSetupEnabledAndIsReviewRoute())
            ? TAGGABLE_DP_ELEMENTS_V2
            : TAGGABLE_DP_ELEMENTS;
      }
      break;

    default:
      {
        if (_.isEmpty(plannerElementSets)) {
          taggableElements = TAGGABLE_PYP_ELEMENTS;
        } else {
          taggableElements = getTaggableElementsWithDynamicLabels({
            elements: TAGGABLE_PYP_ELEMENTS,
            plannerElementSets,
          });
        }
      }
      break;
  }
  return _.filter(
    taggableElements,
    element => !_.includes(excludedElements, element?.value)
  );
};

export const getTaggableElementsMemoized = _.memoize(
  params => getTaggableElements(params),
  params => JSON.stringify(params)
);

export const getAllTaggableElements = ({
  curriculumProgramType,
  plannerElementSets,
  assessmentDetails,
  shouldLocalizeLabel = true,
  t,
  unitPlanFields,
}) => {
  let taggableElements = [];
  switch (curriculumProgramType) {
    case CURRICULUM_TYPE_DP:
      taggableElements = TAGGABLE_DP_ELEMENTS_V2;
      break;
    case CURRICULUM_TYPE_MYP:
      taggableElements = TAGGABLE_MYP_ELEMENTS_V2;
      break;
    case CURRICULUM_TYPE_UBD:
      taggableElements = TAGGABLE_UBD_ELEMENTS;
      break;
    default:
      taggableElements = TAGGABLE_PYP_ELEMENTS_V2;
  }

  if (!_.isEmpty(plannerElementSets)) {
    taggableElements = getTaggableElementsWithDynamicLabels({
      elements: taggableElements,
      plannerElementSets,
    });
  }

  // only return planner elements for which value is not empty
  taggableElements = _.filter(taggableElements, ({ value }) =>
    checkEmptyElementValue({
      fieldKey: value,
      data: assessmentDetails,
      ignoreUnitPlanSubjectCheck: true,
      unitPlanFields,
      checkOnlyValueKeysForBenchmarks: true,
    })
  );

  if (shouldLocalizeLabel) {
    return _.map(taggableElements, element => {
      return {
        ...element,
        label: t(_.get(element, "label")),
      };
    });
  }

  return taggableElements;
};

export const getFieldUids = ({ curriculumProgramType }) => {
  const taggablElements = getTaggableElements({ curriculumProgramType });
  let fieldUids = _.map(
    taggablElements,
    taggableElement => taggableElement.value
  );

  if (curriculumProgramType === "IB_MYP") {
    fieldUids = [...fieldUids, "objectivesMYP", "interdisciplinaryCriteriaMYP"];
  }

  return [...fieldUids, "measureAssessing"];
};

export const getFieldUidsMemoize = _.memoize(
  getFieldUids,
  ({ curriculumProgramType }) => {
    return JSON.stringify({ curriculumProgramType });
  }
);

export const getTaggableElementsWithDynamicLabels = ({
  elements,
  plannerElementSets,
}) => {
  return _.map(elements, element => {
    const isDynamicPlannerElementLabel = getDynamicPlannerElementLabelStatus({
      item: element,
    });

    if (
      plannerElementSets[element.elementType] &&
      isDynamicPlannerElementLabel
    ) {
      return {
        ...element,
        label: plannerElementSets[element.elementType]?.label,
        localisedLabel: plannerElementSets[element.elementType]?.label,
      };
    }
    return element;
  });
};
